import { createFileRoute, Link } from "@tanstack/react-router";
import { FileText, Mail } from "lucide-react";

import { SUPPORT_EMAIL } from "@/lib/contact";
import { createWebPageJsonLd, seo } from "@/lib/seo";

const UPDATED_AT = "May 25, 2026";

const sections = [
	{
		title: "Using StudyOS",
		body: "StudyOS helps you create and review learning materials. You are responsible for the content you upload, the prompts you enter, and how you use generated outputs.",
	},
	{
		title: "Accounts",
		body: "Keep your account information accurate and protect your login credentials. You are responsible for activity that happens through your account.",
	},
	{
		title: "Content and AI outputs",
		body: "You should only upload content you have the right to use. AI-generated content may be incomplete or inaccurate, so review important information before relying on it.",
	},
	{
		title: "Acceptable use",
		body: "Do not misuse StudyOS, attempt to disrupt the service, reverse engineer protected parts of the product, violate others' rights, or use the service for unlawful or harmful activity.",
	},
	{
		title: "Plans and billing",
		body: "Paid features, subscriptions, trials, renewals, and cancellations are shown at checkout or in your account. Taxes and third-party payment terms may apply.",
	},
	{
		title: "Service changes",
		body: "We may improve, change, suspend, or discontinue parts of StudyOS. We will try to give reasonable notice when a material change affects active users.",
	},
	{
		title: "Disclaimers",
		body: "StudyOS is provided as available. We do not guarantee that the service will be uninterrupted, error-free, or suitable for every learning, professional, legal, medical, or financial purpose.",
	},
	{
		title: "Termination",
		body: "You may stop using StudyOS at any time. We may suspend or terminate access if these terms are violated or if we need to protect the service, users, or others.",
	},
];

export const Route = createFileRoute("/(landing)/terms/")({
	component: TermsPage,
	head: ({ match }) => {
		const title = "Terms of Service | StudyOS";
		const description =
			"Read the basic terms for using StudyOS, including accounts, content, acceptable use, billing, and service availability.";

		return seo({
			title,
			description,
			pathname: match.pathname,
			keywords: ["StudyOS terms", "terms of service", "AI learning terms"],
			structuredData: createWebPageJsonLd({
				title,
				description,
				pathname: match.pathname,
			}),
		});
	},
});

function TermsPage() {
	return (
		<div className="min-h-screen bg-background text-foreground">
			<section className="border-b border-border/70 px-4 pt-20 pb-12 md:pt-28 md:pb-16">
				<div className="mx-auto max-w-3xl">
					<div className="mb-6 inline-flex items-center gap-2 rounded-full border border-border bg-muted px-4 py-1.5 text-sm font-medium text-muted-foreground">
						<FileText className="size-4 text-primary" />
						Terms of Service
					</div>
					<h1 className="text-4xl font-bold tracking-tight text-foreground md:text-5xl">
						StudyOS Terms of Service
					</h1>
					<p className="mt-6 text-lg leading-8 text-muted-foreground">
						These terms explain the basic rules for using StudyOS. They are written to
						be short and readable.
					</p>
					<p className="mt-4 text-sm font-medium text-muted-foreground">
						Last updated: {UPDATED_AT}
					</p>
				</div>
			</section>

			<section className="px-4 py-12 md:py-16">
				<article className="mx-auto max-w-3xl space-y-9">
					{sections.map((section) => (
						<section key={section.title}>
							<h2 className="text-2xl font-semibold tracking-tight text-foreground">
								{section.title}
							</h2>
							<p className="mt-3 leading-8 text-muted-foreground">{section.body}</p>
						</section>
					))}

					<section className="rounded-lg border border-border bg-muted/40 p-6 md:p-8">
						<div className="mb-4 inline-flex rounded-lg bg-background p-3">
							<Mail className="size-5 text-primary" />
						</div>
						<h2 className="text-2xl font-semibold tracking-tight text-foreground">
							Questions
						</h2>
						<p className="mt-4 leading-8 text-muted-foreground">
							If you have questions about these terms, contact us at{" "}
							<a
								href={`mailto:${SUPPORT_EMAIL}`}
								className="font-semibold text-primary hover:underline"
							>
								{SUPPORT_EMAIL}
							</a>
							.
						</p>
					</section>

					<div className="flex flex-wrap gap-3 border-t border-border pt-8">
						<Link
							to="/"
							className="inline-flex items-center rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90"
						>
							Back to home
						</Link>
						<Link
							to="/privacy"
							className="inline-flex items-center rounded-full border border-border px-5 py-2.5 text-sm font-semibold text-foreground transition-colors hover:bg-muted"
						>
							Privacy Policy
						</Link>
					</div>
				</article>
			</section>
		</div>
	);
}
